import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Paper,
  Typography,
} from '@mui/material';
import type { TravelerCaptureSource } from '@bandido/shared/types';
import { useCaptureEvidence } from './queries';
import { CaptureSourceViewer } from './CaptureSourceViewer';

export const CaptureEvidenceDialog = ({
  caseId,
  travelerId,
  onClose,
}: {
  caseId: number;
  travelerId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [source, setSource] = useState<TravelerCaptureSource>();
  const evidence = useCaptureEvidence(caseId, travelerId, page);
  return (
    <>
      <Dialog open fullWidth maxWidth="md" onClose={onClose}>
        <DialogTitle>{t('copilot.capture.evidence')}</DialogTitle>
        <DialogContent>
          {evidence.isLoading && <CircularProgress />}
          {evidence.isError && (
            <Alert severity="error" action={<Button onClick={() => evidence.refetch()}>{t('common.retry')}</Button>}>
              {t('copilot.capture.loadError')}
            </Alert>
          )}
          {evidence.data?.items.length === 0 && <Typography>{t('copilot.capture.noEvidence')}</Typography>}
          {evidence.data?.items.map((item) => (
            <Paper key={item.proposalId} variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2">
                {t(`copilot.capture.actions.${item.application.action}`)} ·{' '}
                {new Date(item.application.appliedAt).toLocaleString()}
              </Typography>
              {Object.entries(item.application.fields).map(
                ([key, field]) =>
                  field && (
                    <Typography key={key} variant="body2">
                      {t(`travelers.fields.${key}`)}: {field.value || '—'}{' '}
                      {field.edited && `(${t('copilot.capture.edited')})`}
                    </Typography>
                  ),
              )}
              {item.application.documents.map((document, index) => (
                <Paper key={document.id} variant="outlined" sx={{ p: 1, my: 1 }}>
                  <Typography variant="caption">
                    {t('copilot.capture.includeDocument', { number: index + 1 })}
                  </Typography>
                  {Object.entries(document.fields).map(([key, field]) => (
                    <Typography key={key} variant="body2">
                      {t(`travelers.documents.${key === 'issuingCountry' ? 'country' : key}`)}: {field.value || '—'}{' '}
                      {field.edited && `(${t('copilot.capture.edited')})`}
                    </Typography>
                  ))}
                </Paper>
              ))}
              {item.sources.map((source) => (
                <Button key={source.id} size="small" onClick={() => setSource(source)}>
                  {source.label}
                </Button>
              ))}
            </Paper>
          ))}
          {(evidence.data?.total ?? 0) > 25 && (
            <Pagination
              count={Math.ceil((evidence.data?.total ?? 0) / 25)}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      {source && (
        <CaptureSourceViewer
          key={`${caseId}:${source.id}`}
          caseId={caseId}
          source={source}
          onClose={() => setSource(undefined)}
        />
      )}
    </>
  );
};
